import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';

interface AnalyticsRow {
  readonly contentAnalyticsId: string;
  readonly analyticsCode: string;
  readonly metricType: string;
  readonly category: string | null;
  readonly metricValue: string | null;
  readonly metricCount: number | null;
}

const METRIC_TYPES = ['', 'ASSET_USAGE', 'DOWNLOADS', 'VIEWS', 'DISTRIBUTION_TREND', 'CAMPAIGN_UTILIZATION', 'ASSET_REUSE_RATE', 'COMPLIANCE_METRIC', 'REPOSITORY_GROWTH'];

/**
 * M36 — Content Analytics Dashboard (§31). Register of computed content
 * analytics metrics (T491) across the 8 analytics metric types (asset usage,
 * downloads, views, distribution trend, campaign utilisation, reuse rate,
 * compliance and repository growth), showing the numeric metric value and
 * event count per record. Filterable by metric type. Tenant isolation is
 * enforced server-side (LAW 4).
 */
export function ContentAnalyticsDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<AnalyticsRow[]>([]);
  const [total, setTotal] = useState(0);
  const [metricType, setMetricType] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (metricType) params.set('metricType', metricType);
    const qs = params.toString();
    apiRequest<{ data: AnalyticsRow[]; total: number }>(`/v1/dam/analytics${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, metricType]);

  return (
    <main aria-label="content-analytics-dashboard">
      <h1>{t('m36.analytics.title')}</h1>
      <div className="filters">
        <label>
          {t('m36.analytics.metricLabel')}
          <select aria-label="filter-metric" value={metricType} onChange={(e) => setMetricType(e.target.value)}>
            {METRIC_TYPES.map((s) => <option key={s || 'all'} value={s}>{s || t('m36.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="analytics-count">{t('m36.analytics.count', { count: total })}</p>
          <DataTable<AnalyticsRow>
            ariaLabel="analytics-table"
            testId="analytics-table"
            rowKey={(r) => r.contentAnalyticsId}
            rowDataAttrs={(r) => ({ 'data-metric': r.metricType })}
            rows={rows}
            columns={[
              { key: 'code', header: t('m36.common.code'), render: (r) => r.analyticsCode },
              { key: 'metricType', header: t('m36.analytics.col.metricType'), render: (r) => r.metricType },
              { key: 'category', header: t('m36.common.category'), render: (r) => r.category ?? '' },
              { key: 'value', header: t('m36.analytics.col.value'), render: (r) => r.metricValue ?? '', cellAriaLabel: (r) => `value-${r.contentAnalyticsId}` },
              { key: 'count', header: t('m36.common.count'), render: (r) => r.metricCount ?? '', cellAriaLabel: (r) => `count-${r.contentAnalyticsId}` },
            ]}
          />
        </>
      )}
    </main>
  );
}
